import { useCallback, useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { GraduationCap, BookOpen, CheckCircle2, PlayCircle, Loader2, Award } from "lucide-react";
import { LogoMark } from "@/components/Logo";
import api from "@/lib/api";

const STATUS_BADGE = {
  assigned: ["Assigned", "border-white/20 bg-slate-100 text-muted-foreground"],
  in_progress: ["In progress", "border-amber/50 bg-amber/10 text-amber-700"],
  completed: ["Completed", "border-moss/50 bg-moss/10 text-green-700"],
};

const Stat = ({ label, value, testid }) => (
  <div data-testid={testid} className="rounded-md border border-slate-200 bg-slate-50 px-5 py-4">
    <p className="font-mono text-2xl font-semibold text-slate-900">{value}</p>
    <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mt-1">{label}</p>
  </div>
);

export default function TrainingHub() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [unauth, setUnauth] = useState(false);
  const [mine, setMine] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [starting, setStarting] = useState("");

  const load = useCallback(async () => {
    try {
      const [myRes, courseRes] = await Promise.all([api.get("/training/my"), api.get("/training/courses")]);
      setMine(myRes.data);
      const assigned = new Set(myRes.data.map((a) => a.course_id));
      setCatalog(courseRes.data.filter((c) => !assigned.has(c.id)));
    } catch (err) {
      if (err.response?.status === 401) setUnauth(true);
      else toast.error("Could not load training");
    }
  }, []);

  useEffect(() => {
    const link = document.querySelector('link[rel="icon"]');
    const prev = link?.getAttribute("href");
    link?.setAttribute("href", "/hr-logo-mark.png");
    document.title = "OPERA360 HR — Training Portal";
    return () => {
      if (prev) link?.setAttribute("href", prev);
      document.title = "OPERA360 — Operations & Training OS for Community Portfolios";
    };
  }, []);

  useEffect(() => {
    api.get("/auth/me")
      .then((res) => {
        if (res.data?.role === "manager") { navigate("/company"); return; }
        setUser(res.data); load();
      })
      .catch(() => setUnauth(true));
  }, [load, navigate]);

  const start = async (courseId) => {
    setStarting(courseId);
    try {
      const res = await api.post("/training/start", { course_id: courseId });
      navigate(`/training/course/${res.data.id}`);
    } catch {
      toast.error("Could not start course");
    } finally {
      setStarting("");
    }
  };

  const isAdmin = user?.role !== "member";

  if (unauth) {
    return (
      <div className="min-h-screen workspace-light bg-[#EDF4FB] flex items-center justify-center px-4 relative" data-testid="training-signin">
        <div className="w-full max-w-md">
          <div className="flex justify-center mb-8">
            <Link to="/" aria-label="OPERA360 home"><LogoMark size={44} /></Link>
          </div>
          <div className="technical-card p-8 text-center">
            <span className="inline-flex h-12 w-12 items-center justify-center rounded-sm border border-cadmium/40 bg-cadmium/10 mb-5">
              <GraduationCap size={20} className="text-sky-600" />
            </span>
            <p className="text-xs font-mono font-semibold uppercase tracking-[0.2em] text-slate-900 mb-2">
              Employee Training Portal
            </p>
            <p className="text-sm text-muted-foreground leading-relaxed mb-7">
              Sign in with the account your manager created for you. When a course is assigned to you,
              you'll get an email with a link straight back here.
            </p>
            <Link
              to="/login"
              data-testid="training-signin-btn"
              className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-cadmium px-5 py-3.5 text-sm font-semibold text-white transition-all duration-200 hover:bg-cadmium-hover active:scale-[0.98]"
            >
              Sign In to Training
            </Link>
          </div>
          <p className="mt-6 text-center">
            <Link to="/" className="text-xs font-mono uppercase tracking-widest text-muted-foreground hover:text-sky-600 transition-colors">
              ← Back to site
            </Link>
          </p>
        </div>
      </div>
    );
  }
  const completed = mine.filter((a) => a.status === "completed").length;
  const inProgress = mine.filter((a) => a.status === "in_progress").length;
  const scored = mine.filter((a) => a.best_score != null);
  const avg = scored.length ? Math.round(scored.reduce((s, a) => s + a.best_score, 0) / scored.length) : null;

  return (
    <div className="min-h-screen workspace-light bg-[#EDF4FB]" data-testid="training-hub">
      <header className="sticky top-0 z-40 border-b border-slate-200 bg-[#EDF4FB]/90 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" aria-label="OPERA360 home"><LogoMark size={26} /></Link>
            <span className="font-cabinet font-black tracking-tight text-lg text-slate-900">
              OPERA<span className="text-sky-600">360</span>
            </span>
            <span className="ml-2 rounded-sm border border-cadmium/40 bg-cadmium/10 px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest text-sky-600">
              Training Hub
            </span>
          </div>
          <div className="flex items-center gap-4">
            {isAdmin && (
              <>
                <Link to="/portal" className="text-xs font-mono uppercase tracking-widest text-muted-foreground hover:text-sky-600 transition-colors">Portal →</Link>
                <Link to="/admin" className="text-xs font-mono uppercase tracking-widest text-muted-foreground hover:text-sky-600 transition-colors">CRM →</Link>
              </>
            )}
            <span className="hidden sm:block text-xs font-mono text-muted-foreground">{user?.email}</span>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="mb-8">
          <p className="eyebrow mb-3 flex items-center gap-3"><span className="inline-block h-px w-10 bg-cadmium" /> TRAIN</p>
          <h1 className="font-cabinet text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">
            Welcome back{user?.name ? `, ${user.name.split(" ")[0]}` : ""}.
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">Work through your assigned courses and pass the final exams to certify.</p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          <Stat label="My Courses" value={mine.length} testid="hub-stat-total" />
          <Stat label="In Progress" value={inProgress} testid="hub-stat-progress" />
          <Stat label="Completed" value={completed} testid="hub-stat-completed" />
          <Stat label="Avg Best Score" value={avg != null ? `${avg}%` : "—"} testid="hub-stat-avg" />
        </div>

        <p className="text-xs font-mono font-semibold uppercase tracking-[0.24em] text-muted-foreground mb-4">My Training</p>
        {mine.length === 0 ? (
          <div className="technical-card p-12 text-center mb-10" data-testid="hub-empty">
            <GraduationCap size={26} className="mx-auto text-muted-foreground/40 mb-3" />
            <p className="text-sm text-muted-foreground">No courses assigned yet.</p>
            <p className="mt-1 text-xs font-mono uppercase tracking-wider text-muted-foreground/60">Start one from the catalog below.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
            {mine.map((a) => {
              const [label, cls] = STATUS_BADGE[a.status] || STATUS_BADGE.assigned;
              return (
                <div key={a.id} data-testid={`hub-course-${a.id}`} className="technical-card p-6 flex flex-col">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-[10px] font-mono uppercase tracking-widest text-sky-600">{a.category}</span>
                    <span className={`rounded-sm border px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider ${cls}`}>{label}</span>
                  </div>
                  <p className="font-cabinet text-lg font-bold text-slate-900 leading-snug">{a.course_title}</p>
                  <div className="mt-4 mb-2 flex items-center justify-between text-[11px] font-mono text-muted-foreground">
                    <span>{a.done_lessons}/{a.total_lessons} lessons</span>
                    {a.best_score != null && (
                      <span className={a.passed ? "text-green-700" : "text-amber-700"}>best {a.best_score}%{a.attempts > 1 ? ` · ${a.attempts} tries` : ""}</span>
                    )}
                  </div>
                  <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden mb-4">
                    <div className={`h-full rounded-full ${a.passed ? "bg-moss" : "bg-cadmium"}`} style={{ width: `${a.progress_pct}%` }} />
                  </div>
                  {a.due_date && (
                    <p data-testid={`hub-due-${a.id}`} className={`mb-4 text-[10px] font-mono uppercase tracking-wider ${a.overdue ? "text-amber-700" : "text-muted-foreground/70"}`}>
                      Due {a.due_date}{a.overdue ? " — overdue" : ""}
                    </p>
                  )}
                  <button
                    data-testid={`hub-continue-${a.id}`}
                    onClick={() => navigate(`/training/course/${a.id}`)}
                    className="mt-auto inline-flex items-center justify-center gap-2 rounded-md bg-cadmium px-4 py-2.5 text-xs font-mono uppercase tracking-widest text-white hover:bg-cadmium-hover transition-colors"
                  >
                    {a.status === "completed" ? <Award size={14} /> : <PlayCircle size={14} />}
                    {a.status === "completed" ? "Review" : a.done_lessons > 0 ? "Continue" : "Start"}
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {catalog.length > 0 && (
          <>
            <p className="text-xs font-mono font-semibold uppercase tracking-[0.24em] text-muted-foreground mb-4">Course Catalog</p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {catalog.map((c) => (
                <div key={c.id} data-testid={`catalog-course-${c.id}`} className="rounded-md border border-slate-200 bg-slate-50 p-6 flex flex-col">
                  <span className="text-[10px] font-mono uppercase tracking-widest text-sky-600 mb-2">{c.category}</span>
                  <p className="font-cabinet text-base font-bold text-slate-900 leading-snug">{c.title}</p>
                  <p className="mt-2 text-xs text-muted-foreground leading-relaxed line-clamp-3">{c.description}</p>
                  <p className="mt-3 text-[10px] font-mono uppercase tracking-wider text-muted-foreground/70 flex items-center gap-1.5">
                    <BookOpen size={11} /> {c.modules} modules · {c.lessons} lessons · {c.questions} exam questions
                  </p>
                  <button
                    data-testid={`catalog-start-${c.id}`}
                    onClick={() => start(c.id)}
                    disabled={starting === c.id}
                    className="mt-5 inline-flex items-center justify-center gap-2 rounded-md border border-cadmium/50 px-4 py-2.5 text-xs font-mono uppercase tracking-widest text-sky-600 hover:bg-cadmium/10 transition-colors disabled:opacity-60"
                  >
                    {starting === c.id ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                    Start Course
                  </button>
                </div>
              ))}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
